/**
 * @fileoverview TestResult API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the TestResult module.
 * Single source of truth for all test result-related API definitions.
 */

import { z } from 'zod';
import {
	createContract,
	extractRoutes,
	createPaginatedSchema,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';
import { OnboardingDataSchema, OnboardingContextSchema } from '../onboarding.schemas';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Test Status Enum
 */
export enum TestStatus {
	PENDING = 'pending',
	PROCESSING = 'processing',
	COMPLETED = 'completed',
	FAILED = 'failed',
}

/**
 * Test Type Enum
 */
export enum TestType {
	LEARNING_TYPE_TEST = 'learning_type_test',
	PERSONALITY_TEST = 'personality_test',
	SKILLS_ASSESSMENT = 'skills_assessment',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Test Scores Schema
 *
 * Percentage-based scores for learning types and additional assessments.
 * All scores are **percentages** (0-100%), calculated as:
 *   `(rawScore / maxRawScore) * 100`
 *
 * Where:
 * - rawScore = sum of answers (1-5) for all questions in the section
 * - maxRawScore = questionCount * 5
 *
 * Example: 10 questions with all 5s = 50/50 = 100%
 *
 * @see SectionMetadata for dynamic question counts per section
 */
export const TestScoresSchema = z.object({
	// Core learning types (Phase 1)
	visual: z.number().int().min(0).max(100).optional(),
	auditory: z.number().int().min(0).max(100).optional(),
	reading: z.number().int().min(0).max(100).optional(),
	kinesthetic: z.number().int().min(0).max(100).optional(),
	integrated: z.number().int().min(0).max(100).optional(),
	// Additional assessments (Phase 2)
	giftedness: z.number().int().min(0).max(100).optional(),
	auditoryProcessing: z.number().int().min(0).max(100).optional(),
	highSensitivity: z.number().int().min(0).max(100).optional(),
	adhd: z.number().int().min(0).max(100).optional(),
});
export type TestScores = z.infer<typeof TestScoresSchema>;

/**
 * Basic Info Schema
 * Student/child information from test form
 */
export const BasicInfoSchema = z.object({
	childName: z.string().optional(),
	childAge: z.union([z.number(), z.string()]).optional(),
	childSchool: z.string().optional(),
	childSituation: z.string().optional(),
	childInterests: z.string().optional(), // Optional field for child's interests/hobbies
	childGender: z.enum(['male', 'female', 'other']).optional(), // Gender for correct pronouns
	// Legacy fields (for backward compatibility)
	grade: z.string().optional(),
	situation: z.string().optional(),
});
export type BasicInfo = z.infer<typeof BasicInfoSchema>;

/**
 * Test Results Schema
 * Calculated results with learning type and scores
 */
export const TestResultsSchema = z.object({
	learningType: z.string().optional(),
	scores: TestScoresSchema.optional(),
});
export type TestResults = z.infer<typeof TestResultsSchema>;

/**
 * Test Data Schema
 * Complete test data structure including basic info and results
 */
export const TestDataSchema = z.object({
	basicInfo: BasicInfoSchema.optional(),
	results: TestResultsSchema.optional(),
});
export type TestData = z.infer<typeof TestDataSchema>;



/**
 * Test Answers Schema
 * Raw answers from the questionnaire (flexible structure)
 */
export const TestAnswersSchema = z.union([TestDataSchema, z.record(z.string(), z.unknown())]);
export type TestAnswers = z.infer<typeof TestAnswersSchema>;

/**
 * Create TestResult Request Schema
 */
export const CreateTestResultSchema = z.object({
	testId: z.string().min(1, 'Test ID is required'),
	testType: z.enum(TestType).optional().default(TestType.LEARNING_TYPE_TEST),
	studentName: z.string().min(1, 'Student name is required'),
	studentAge: z.string().min(1, 'Student age is required'),
	school: z.string().optional(),
	situation: z.string().optional(),
	customerEmail: z.email('Invalid email format'),
	answers: TestAnswersSchema,
	scores: TestScoresSchema.optional(),
});
export type CreateTestResultPayload = z.infer<typeof CreateTestResultSchema>;

/**
 * Update TestResult Request Schema
 */
export const UpdateTestResultSchema = z.object({
	status: z.enum(TestStatus).optional(),
	scores: TestScoresSchema.optional(),
	paymentCompleted: z.boolean().optional(),
	paymentIntentId: z.string().optional(),
	paymentAmount: z.number().optional(),
});
export type UpdateTestResultPayload = z.infer<typeof UpdateTestResultSchema>;

/**
 * TestResult Entity Schema (API Response)
 *
 * TestResult is created on test completion (before payment) to enable remarketing.
 * customerEmail is null until payment is completed.
 *
 * Includes:
 * - basicInfo: Complete child information (childName, childAge, childSchool, childSituation, childInterests, childGender)
 * - onboardingContext: Structured onboarding data (challenges, goals, parent feeling, timestamps)
 * - onboarding: DEPRECATED - Legacy field for backward compatibility
 */
export const TestResultSchema = z.object({
	id: z.uuid(),
	testId: z.string(),
	status: z.enum(TestStatus),
	testType: z.enum(TestType),
	studentName: z.string(),
	studentAge: z.string(),
	school: z.string().nullable().optional(),
	situation: z.string().nullable().optional(),
	customerEmail: z.email().nullable(), // Null until payment
	answers: TestAnswersSchema,
	scores: TestScoresSchema.nullable().optional(),
	// Basic info (structured - NEW)
	basicInfo: BasicInfoSchema.nullable().optional(),
	// Onboarding context (structured - NEW)
	onboardingContext: OnboardingContextSchema.nullable().optional(),
	// Onboarding data (DEPRECATED - use onboardingContext instead)
	onboarding: OnboardingDataSchema.nullable().optional(),
	paymentRequired: z.boolean(),
	paymentCompleted: z.boolean(),
	paymentIntentId: z.string().nullable().optional(),
	paymentAmount: z.number().nullable().optional(),
	userId: z.uuid().nullable().optional(),
	startedAt: z.iso.datetime().nullable().optional(),
	completedAt: z.iso.datetime().nullable().optional(),
	processingTimeMs: z.number().nullable().optional(),
	ipAddress: z.string().nullable().optional(),
	userAgent: z.string().nullable().optional(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type TestResult = z.infer<typeof TestResultSchema>;

/**
 * TestResult ID Parameter Schema
 */
export const TestResultIdParamSchema = z.object({
	id: z.uuid('Invalid test result ID format'),
});
export type TestResultIdParam = z.infer<typeof TestResultIdParamSchema>;

/**
 * TestResult by TestId Parameter Schema
 */
export const TestIdParamSchema = z.object({
	testId: z.string().min(1, 'Test ID is required'),
});
export type TestIdParam = z.infer<typeof TestIdParamSchema>;

/**
 * List TestResults Query Parameters Schema
 */
export const ListTestResultsQuerySchema = z.object({
	page: z.coerce.number().int().positive().optional().default(1),
	limit: z.coerce.number().int().positive().max(100).optional().default(10),
	status: z.enum(TestStatus).optional(),
	testType: z.enum(TestType).optional(),
	userId: z.uuid().optional(),
	customerEmail: z.email().optional(),
});
export type ListTestResultsQuery = z.infer<typeof ListTestResultsQuerySchema>;

/**
 * Paginated TestResults Response Schema
 */
export const PaginatedTestResultsSchema = createPaginatedSchema(TestResultSchema);
export type PaginatedTestResults = z.infer<typeof PaginatedTestResultsSchema>;

// ============================================================================
// API RESPONSE WRAPPERS (Consistent Response Format)
// ============================================================================

/**
 * Single TestResult API Response
 * { success: true, data: TestResult, timestamp: string }
 */
export const TestResultApiResponseSchema = ApiSuccessResponseSchema(TestResultSchema);

/**
 * Paginated TestResults API Response
 * { success: true, data: TestResult[], meta: PaginationMeta, timestamp: string }
 */
export const PaginatedTestResultsApiResponseSchema = ApiPaginatedResponseSchema(TestResultSchema);

/**
 * Delete TestResult API Response
 * { success: true, data: { message: string }, timestamp: string }
 */
const DeleteTestResultDataSchema = z.object({ message: z.string() });
export const DeleteTestResultApiResponseSchema = ApiSuccessResponseSchema(
	DeleteTestResultDataSchema,
);
export type DeleteTestResultResponse = z.infer<typeof DeleteTestResultApiResponseSchema>;

// ============================================================================
// API CONTRACT
// ============================================================================

/**
 * TestResult API Contract
 *
 * Defines the complete API surface for the TestResult module.
 * Both frontend and backend use this as the single source of truth.
 */
export const TEST_RESULT_API_CONTRACT = createContract('/test-results', {
	/**
	 * POST /test-results
	 * Create a new test result
	 */
	create: {
		method: 'POST' as const,
		path: '/',
		body: CreateTestResultSchema,
		response: TestResultSchema,
	},

	/**
	 * GET /test-results
	 * List all test results with pagination and filtering
	 */
	list: {
		method: 'GET' as const,
		path: '/',
		query: ListTestResultsQuerySchema,
		response: PaginatedTestResultsSchema,
	},

	/**
	 * GET /test-results/:id
	 * Get a single test result by ID
	 */
	getById: {
		method: 'GET' as const,
		path: '/:id',
		params: TestResultIdParamSchema,
		response: TestResultSchema,
	},

	/**
	 * GET /test-results/by-test-id/:testId
	 * Get a test result by testId
	 */
	getByTestId: {
		method: 'GET' as const,
		path: '/by-test-id/:testId',
		params: TestIdParamSchema,
		response: TestResultSchema,
	},

	/**
	 * PATCH /test-results/:id
	 * Update a test result
	 */
	update: {
		method: 'PATCH' as const,
		path: '/:id',
		params: TestResultIdParamSchema,
		body: UpdateTestResultSchema,
		response: TestResultSchema,
	},

	/**
	 * DELETE /test-results/:id
	 * Delete a test result
	 */
	delete: {
		method: 'DELETE' as const,
		path: '/:id',
		params: TestResultIdParamSchema,
		response: DeleteTestResultApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const TEST_RESULT_ROUTES = extractRoutes(TEST_RESULT_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type TestResultApiContract = typeof TEST_RESULT_API_CONTRACT;

// Re-export OnboardingData types from onboarding.schemas for convenience
export type { OnboardingData } from '../onboarding.schemas';
